import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BellRing, Check, CheckCheck, Link as LinkIcon } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import {
  acknowledgeSystemAlert,
  getSystemAlerts,
  resolveSystemAlert,
  type SystemAlert,
  type SystemAlertsList,
} from '../../lib/systemAlertsApi';
import { RelativeTime } from '../ui/RelativeTime';

/**
 * Settings → Alerte: what the app could not do because a dependency failed —
 * OpenAI out of credit, an invalid key, ANAF or Drive authorisation expired,
 * the mail server refusing.
 *
 * The list is the record; the push and the email are how admins find out.
 * An alert closes itself when the same service answers again, or by hand.
 */

export const SYSTEM_ALERTS_KEY = ['system-alerts'] as const;
export const SYSTEM_ALERTS_SUMMARY_KEY = ['system-alerts', 'summary'] as const;

const SERVICE_LABEL: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  drive: 'Google Drive',
  anaf: 'ANAF SPV',
  smtp: 'Email (SMTP)',
};

const SERVICE_LINK: Record<string, string> = {
  openai: 'https://platform.openai.com/settings/organization/billing/overview',
  anthropic: 'https://console.anthropic.com/settings/billing',
};

function SeverityBadge({ severity }: { severity: SystemAlert['severity'] }) {
  const { t } = useI18n();
  const critical = severity === 'CRITICAL';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
        critical ? 'bg-red-500/15 text-red-500' : 'bg-amber-500/15 text-amber-500'
      }`}
    >
      <AlertTriangle size={11} />
      {critical ? t('alerts.severity.critical') : t('alerts.severity.warning')}
    </span>
  );
}

function AlertCard({
  alert,
  onAcknowledge,
  onResolve,
  busy,
}: {
  alert: SystemAlert;
  onAcknowledge?: () => void;
  onResolve?: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  const open = alert.resolvedAt == null;
  // With the Firmio AI subscription the provider account is Firmio's.
  const link = alert.managedByFirmio ? undefined : SERVICE_LINK[alert.service];
  return (
    <li
      className={`rounded-lg border p-4 ${
        open
          ? alert.severity === 'CRITICAL'
            ? 'border-red-500/40 bg-red-500/5'
            : 'border-amber-500/40 bg-amber-500/5'
          : 'border-gray-200 bg-white opacity-80'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <SeverityBadge severity={alert.severity} />
        <span className="text-xs font-medium text-gray-500">
          {SERVICE_LABEL[alert.service] ?? alert.service}
        </span>
        {alert.count > 1 && (
          <span className="text-xs text-gray-500">
            · {t('alerts.count', { count: alert.count })}
          </span>
        )}
      </div>
      <h3 className="mt-2 text-[15px] font-semibold text-gray-900">{alert.title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-gray-700">{alert.message}</p>
      <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
        <span>
          {t('alerts.firstSeen')} <RelativeTime iso={alert.firstSeenAt} />
        </span>
        <span>
          {t('alerts.lastSeen')} <RelativeTime iso={alert.lastSeenAt} />
        </span>
        {alert.resolvedAt && (
          <span>
            {t('alerts.resolvedAt')} <RelativeTime iso={alert.resolvedAt} />
          </span>
        )}
        {alert.acknowledgedAt && alert.acknowledgedBy && (
          <span className="inline-flex items-center gap-1">
            <Check size={12} /> {t('alerts.acknowledgedBy', { name: alert.acknowledgedBy.name })}
          </span>
        )}
      </p>
      {open && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <LinkIcon size={14} /> {t('alerts.openProvider')}
            </a>
          )}
          {!alert.acknowledgedAt && onAcknowledge && (
            <button
              type="button"
              onClick={onAcknowledge}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60"
            >
              <Check size={14} /> {t('alerts.acknowledge')}
            </button>
          )}
          {onResolve && (
            <button
              type="button"
              onClick={onResolve}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            >
              <CheckCheck size={14} /> {t('alerts.resolve')}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export function AlertsSettings() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: SYSTEM_ALERTS_KEY,
    queryFn: getSystemAlerts,
    refetchInterval: 60_000,
  });

  const settle = (list: SystemAlertsList) => {
    qc.setQueryData(SYSTEM_ALERTS_KEY, list);
    void qc.invalidateQueries({ queryKey: SYSTEM_ALERTS_SUMMARY_KEY });
  };
  const acknowledge = useMutation({ mutationFn: acknowledgeSystemAlert, onSuccess: settle });
  const resolve = useMutation({ mutationFn: resolveSystemAlert, onSuccess: settle });
  const busy = acknowledge.isPending || resolve.isPending;

  const data = query.data;

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-gray-500">{t('alerts.intro')}</p>
      <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
        <BellRing size={14} /> {t('alerts.deliveryHint')}
      </p>

      {query.isLoading && <p className="mt-6 text-sm text-gray-500">{t('alerts.loading')}</p>}
      {query.isError && (
        <p className="mt-6 text-sm text-red-600">
          {t('alerts.loadError')}
          {query.error instanceof Error ? `: ${query.error.message}` : ''}.
        </p>
      )}

      {data && (
        <>
          <h2 className="mt-6 text-base font-semibold text-gray-900">
            {t('alerts.openTitle', { count: data.open.length })}
          </h2>
          {data.open.length === 0 ? (
            <p className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-600">
              {t('alerts.none')}
            </p>
          ) : (
            <ul className="mt-3 space-y-3">
              {data.open.map((alert) => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  busy={busy}
                  onAcknowledge={() => acknowledge.mutate(alert.id)}
                  onResolve={() => resolve.mutate(alert.id)}
                />
              ))}
            </ul>
          )}

          {data.resolved.length > 0 && (
            <>
              <h2 className="mt-8 text-base font-semibold text-gray-900">
                {t('alerts.resolvedTitle')}
              </h2>
              <ul className="mt-3 space-y-3">
                {data.resolved.map((alert) => (
                  <AlertCard key={alert.id} alert={alert} busy={busy} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
